import type { ToggleGroupMultipleProps } from "@radix-ui/react-toggle-group";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import clsx from "clsx";
import type { ToggleGroupItemProps } from "../toggleGroup/ToggleGroup";
import { toggleGroup, toggleItem } from "../toggleGroup/ToggleGroup.css";

/** Props for the MultiToggleGroup component. */
export interface MultiToggleGroupProps extends Omit<
  ToggleGroupMultipleProps,
  "type" | "value" | "onValueChange" | "onChange"
> {
  /** The items to render as toggle buttons. */
  items: ToggleGroupItemProps[];
  /** The values switched on. */
  values: string[];
  /** Called with the values switched on after a click. */
  onChange: (values: string[]) => void;
  /** Whether the entire group is disabled. */
  disabled?: boolean;
  /** Whether the last item on can be switched off too. */
  allowNone?: boolean;
}

/**
 * Multi-select segmented control: ToggleGroup's look, any number of items on
 * at once, but never none unless `allowNone`. Wraps Radix ToggleGroup for
 * accessibility.
 */
export function MultiToggleGroup({
  items,
  values,
  onChange,
  disabled = false,
  allowNone = false,
  className,
  ...rest
}: MultiToggleGroupProps) {
  function handleValueChange(next: string[]) {
    // Unless allowNone, a click that would switch off the last item is
    // ignored, as ToggleGroup keeps its one selection.
    if (allowNone || next.length > 0) onChange(next);
  }

  return (
    <ToggleGroupPrimitive.Root
      type="multiple"
      value={values}
      onValueChange={handleValueChange}
      disabled={disabled}
      className={clsx(toggleGroup, className)}
      {...rest}
    >
      {items.map((item) => (
        <ToggleGroupPrimitive.Item
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className={toggleItem}
        >
          {item.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
